<template>
  <div class="relative bg-black">
    <div
      ref="track"
      class="flex snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      :aria-roledescription="items.length > 1 ? 'karuselli' : undefined"
      @scroll.passive="onScroll"
    >
      <div
        v-for="(item, i) in items"
        :key="i"
        class="flex w-full shrink-0 snap-center items-center justify-center"
        :aria-label="items.length > 1 ? `${i + 1} / ${items.length}` : undefined"
      >
        <video
          v-if="item.type === 'VIDEO' && item.video"
          ref="videos"
          :src="item.video"
          :poster="item.image"
          class="max-h-[75vh] w-full object-contain"
          controls
          muted
          playsinline
          loop
          preload="none"
          data-jest="instagram-video"
        />
        <img
          v-else
          :src="item.image"
          :alt="i === 0 ? post.caption || 'Instagram-julkaisu' : ''"
          loading="lazy"
          decoding="async"
          class="max-h-[75vh] w-full object-contain"
        >
      </div>
    </div>

    <template v-if="items.length > 1">
      <button
        v-if="current > 0"
        type="button"
        class="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-1.5 text-black shadow hover:bg-white"
        aria-label="Edellinen kuva"
        @click="go(current - 1)"
      >
        <ChevronLeftIcon class="size-5" />
      </button>
      <button
        v-if="current < items.length - 1"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-1.5 text-black shadow hover:bg-white"
        aria-label="Seuraava kuva"
        @click="go(current + 1)"
      >
        <ChevronRightIcon class="size-5" />
      </button>
      <div class="pointer-events-none absolute bottom-3 left-0 right-0 flex justify-center gap-1.5" aria-hidden="true">
        <span
          v-for="(_, i) in items"
          :key="i"
          class="size-1.5 rounded-full transition-colors"
          :class="i === current ? 'bg-white' : 'bg-white/45'"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/solid'
import type { InstagramMedia, InstagramPost } from '~/types/instagram'

const props = defineProps<{ post: InstagramPost }>()

/** Feeds written before the modal data have only the cropped image */
const items = computed<InstagramMedia[]>(() => props.post.media?.length ? props.post.media : [{ type: 'IMAGE', image: props.post.image }])

const track = ref<HTMLElement | null>(null)
const videos = ref<HTMLVideoElement[]>([])
const current = ref(0)

const onScroll = () => {
  const el = track.value
  if (el) current.value = Math.round(el.scrollLeft / el.clientWidth)
}
const go = (index: number) => {
  const el = track.value
  if (el) el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' })
}

// Videos play muted while in view (the viewer turns the sound on from the controls), not with reduced motion
let observer: IntersectionObserver | null = null
onMounted(() => {
  if (!videos.value.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const video = entry.target as HTMLVideoElement
      if (entry.isIntersecting) video.play().catch(() => {})
      else video.pause()
    }
  }, { threshold: 0.6 })
  videos.value.forEach(video => observer!.observe(video))
})
onBeforeUnmount(() => observer?.disconnect())
</script>
